import React, { useState, useContext, useCallback } from "react";

import { UserType } from "../utils/constants";
import { logError } from "../utils/appUtils";
import { getUsableCarrierJunctions } from "../utils/carrierUserUtils";

// Define the context
const UserContext = React.createContext();

export const UserProvider = ({ children }) => {
  const [appUser, setAppUserInternal] = useState(null);

  // Update both React state and window.appUser
  const setAppUser = (newUser) => {
    setAppUserInternal(newUser);
    window.appUser = newUser;
    try {
      if (newUser?.reloadYardId) {
        localStorage.setItem("reloadYardId", newUser.reloadYardId);
      }
      // The stored pair names the carrier session to resume, so it moves
      // together: a yard switch that changes the represented carrier must
      // update both, or a reload resumes the new yard under the old company —
      // or, via the yard's other junction, in the other portal. A session
      // without a carrier (manager, customer) clears it rather than leaving a
      // previous carrier user's half-pair behind.
      if (newUser?.carrierId) {
        localStorage.setItem("carrierId", newUser.carrierId);
      } else if (newUser) {
        localStorage.removeItem("carrierId");
      }
    } catch (err) {
      logError("UserProvider:setAppUser", err, false);
    }
  };

  const isAuthorized = useCallback(
    (permission) => {
      const userType = appUser?.type;
      if (userType === UserType.MANAGER || userType === UserType.ADMIN) {
        return true;
      }
      if (typeof appUser?.permissionList === "object") {
        return !!appUser.permissionList[permission];
      }
      return false;
    },
    [appUser],
  );

  const isMultiTenant = useCallback(() => {
    // Mirror the accessibility filters used at login (setupCustomerData /
    // setupCarrierData) so blocked or inactive junctions never inflate the
    // count.
    if (Array.isArray(appUser?.reloadYardAccounts?.items)) {
      return (
        appUser.reloadYardAccounts.items.filter(
          (account) => account.active === "true",
        ).length > 1
      );
    }
    if (Array.isArray(appUser?.reloadYardCarriers?.items)) {
      // The same split login uses, so the yard switcher never offers a yard
      // whose junction login would have skipped.
      return (
        getUsableCarrierJunctions(appUser.reloadYardCarriers.items).usable
          .length > 1
      );
    }
    return false;
  }, [appUser]);

  return (
    <UserContext.Provider
      value={{ appUser, setAppUser, isAuthorized, isMultiTenant }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the context
export const useUser = () => useContext(UserContext);

export default UserContext;
